import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

/**
 * A hook that manages server-side pagination, sorting, and search for large datasets.
 *
 * Instead of fetching all records and sorting/filtering client-side, this hook
 * fetches a single page at a time from the server with sort and criteria parameters.
 *
 * @param {Object} config
 * @param {Function} config.fetchFn - The GraphQL getter function (e.g., getStreams, getDevices, getAppliances)
 * @param {Function} config.mapFn - Function to map raw records to table row format
 * @param {string[]} config.fetchFields - Fields to request from the server
 * @param {Object} [config.defaultSort] - Default sort: { field: 'name', direction: 'asc' }
 * @param {number} [config.defaultRowsPerPage=10] - Default rows per page
 * @param {Object} [config.sortFieldMap] - Maps table column field names to backend field names
 * @param {Object} [config.extraOptions] - Additional options merged into every fetch call
 *  (e.g. Current Stream Status report sorting by dynamic zones { zone_priority: 'Zone A' })
 * @param {Function} [config.onError] - Error callback
 */
export const useServerPagination = ({
  fetchFn,
  mapFn,
  fetchFields,
  defaultSort = { field: 'name', direction: 'asc' },
  defaultRowsPerPage = 10,
  sortFieldMap = {},
  enableSort = false,
  extraOptions = {},
  onError,
}) => {
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [sortField, setSortField] = useState(defaultSort.field);
  const [sortDirection, setSortDirection] = useState(defaultSort.direction);
  const [criteria, setCriteria] = useState({});
  // The alchemy Table manages its own internal page state. The `currentPage` prop
  // seeds that state only on mount/remount — changing the prop after mount has no
  // effect. Incrementing this key (used as a React `key` prop on the Table) causes
  // React to unmount and remount the component, starting at whatever `currentPage`
  // is at that moment. handleSearch/clearSearch use this to reset to page 1;
  // resetToPage uses it to navigate to an arbitrary page.
  const [paginationResetKey, setPaginationResetKey] = useState(0);

  const fetchIdRef = useRef(0);
  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  const sortFieldRef = useRef(sortField);
  const sortDirectionRef = useRef(sortDirection);

  // Always keep extraOptionsRef current so fetchPage can read it without a dep.
  // extraOptionsToken (stringified) is used as an effect dep to trigger refetches
  // when the extra options change value — callers should memoize extraOptions.
  const extraOptionsRef = useRef(extraOptions);
  // eslint-disable-next-line react-hooks/refs -- intentional ref write outside effect, safe pattern
  extraOptionsRef.current = extraOptions;

  const extraOptionsToken = useMemo(() => JSON.stringify(extraOptions), [extraOptions]);

  const fetchPage = useCallback(async (page, perPage, sort, dir, searchCriteria) => {
    const thisId = ++fetchIdRef.current;
    setLoading(true);

    try {
      const options = {
        pagination: {
          start_on_page: page,
          count_per_page: perPage,
        },
        ...(enableSort ? { sort: { field: sort, direction: dir } } : {}),
        ...extraOptionsRef.current,
      };

      const { query, execute } = fetchFn(null, searchCriteria, options);
      if (Array.isArray(fetchFields) && fetchFields.length > 0) {
        query.addReturnProperties(...fetchFields);
      }

      const res = await execute();

      // Stale response guard — a newer fetch was triggered while this one was in-flight
      if (thisId !== fetchIdRef.current) return;

      const data = Array.isArray(res?.data) ? res.data : [];
      const paginationInfo = res?.pagination || {};

      const mapped = mapFn ? mapFn(data) : data;
      setRows(mapped);
      setTotalRows(paginationInfo.total_records || 0);
    } catch (error) {
      if (thisId !== fetchIdRef.current) return;
      if (onErrorRef.current) onErrorRef.current(error);
    } finally {
      if (thisId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    fetchFn,
    fetchFields,
    mapFn,
  ]);

  // Fetch whenever pagination or search criteria changes.
  // Sort changes are handled entirely by the direct fetchPage call in handleSort —
  // keeping sortField/sortDirection out of these deps prevents a second effect-triggered
  // fetch (with a potentially stale currentPage) racing against the direct call.
  // sortFieldRef/sortDirectionRef are read directly so the effect always uses current
  // sort values without needing them as deps.
  useEffect(() => {
    fetchPage(currentPage, rowsPerPage, sortFieldRef.current, sortDirectionRef.current, criteria);
  }, [
    currentPage,
    rowsPerPage,
    criteria,
    fetchPage,
    extraOptionsToken,
  ]);

  const handleSort = useCallback((column, direction) => {
    const backendField = sortFieldMap[column.field || column.selector] || column.field;
    if (!backendField) return;
    setSortField(backendField);
    setSortDirection(direction);
    sortFieldRef.current = backendField;
    sortDirectionRef.current = direction;
    setCurrentPage(1);
    fetchPage(1, rowsPerPage, backendField, direction, criteria);
  }, [
    sortFieldMap,
    fetchPage,
    rowsPerPage,
    criteria,
  ]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  /**
   * Navigates to a specific page and resets the Table's internal pagination UI
   * by incrementing paginationResetKey (remounts the Table). Use this instead of
   * handlePageChange when navigating programmatically from outside the Table
   * (e.g. deep-link navigation), since handlePageChange alone fetches the right
   * data but cannot update the Table's own page counter.
   */
  const resetToPage = useCallback((page) => {
    setCurrentPage(page);
    setPaginationResetKey((k) => k + 1);
  }, []);

  const handleRowsPerPageChange = useCallback((perPage) => {
    setRowsPerPage(perPage);
    setCurrentPage(1);
  }, []);

  const handleSearch = useCallback((searchCriteria) => {
    setCriteria(searchCriteria);
    setCurrentPage(1);
    setPaginationResetKey((k) => k + 1);
  }, []);

  const clearSearch = useCallback(() => {
    setCriteria({});
    setCurrentPage(1);
    setPaginationResetKey((k) => k + 1);
  }, []);

  const reset = useCallback(() => {
    setRows([]);
    setLoading(true);
    setCriteria({});
    setCurrentPage(1);
    setPaginationResetKey((k) => k + 1);
    setSortField(defaultSort.field);
    setSortDirection(defaultSort.direction);
    sortFieldRef.current = defaultSort.field;
    sortDirectionRef.current = defaultSort.direction;
  }, [defaultSort.field, defaultSort.direction]);

  const setSortState = useCallback((field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    sortFieldRef.current = field;
    sortDirectionRef.current = direction;
    setCurrentPage(1);
    fetchPage(1, rowsPerPage, field, direction, criteria);
  }, [
    fetchPage,
    rowsPerPage,
    criteria,
  ]);

  const refetchCurrentPage = useCallback(() => {
    fetchPage(currentPage, rowsPerPage, sortField, sortDirection, criteria);
  }, [
    fetchPage,
    currentPage,
    rowsPerPage,
    sortField,
    sortDirection,
    criteria,
  ]);

  /**
   * Fetches all records (no pagination) with a minimal field set and returns them.
   * Used for server-side "select all" so the parent can collect all IDs/names without
   * loading the full dataset into the table.
   *
   * @param {string[]} fields Fields to request (defaults to ['id', 'name']).
   * @returns {Promise<Array>} Array of raw records from the server.
   */
  const fetchAll = useCallback(async (fields = ['id', 'name']) => {
    try {
      const options = {
        ...(enableSort ? { sort: { field: sortFieldRef.current, direction: sortDirectionRef.current } } : {}),
        ...extraOptionsRef.current,
      };
      const { query, execute } = fetchFn(null, criteria, options);
      query.addReturnProperties(...fields);
      const res = await execute();
      return Array.isArray(res?.data) ? res.data : [];
    } catch {
      return [];
    }
  }, [
    fetchFn,
    criteria,
    enableSort,
  ]);

  return {
    rows,
    totalRows,
    loading,
    currentPage,
    rowsPerPage,
    paginationResetKey,
    sortField,
    sortDirection,
    handleSort,
    setSortState,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    clearSearch,
    reset,
    resetToPage,
    refetchCurrentPage,
    fetchAll,
    setRows,
    setTotalRows,
  };
};
